import DAO from '../dao/dao'
import { ConfigResponse } from './config.types'
import CookiesManager from '../cookies/CookiesManager'
import { CLASSY_ATTR, CLASSY_HREF } from '../crawler/crawler.constants'

interface ConfigServiceProps {
  win: Window
  cookiesManager: CookiesManager
}

export interface Configs {
  [campaignId: string]: ConfigResponse
}

export default class ConfigService {
  private props: ConfigServiceProps
  private dao: DAO
  private destroyed = false

  constructor(props: ConfigServiceProps) {
    this.props = props
    this.dao = new DAO({ win: props.win })
  }

  public destroy(): void {
    if (this.destroyed) {
      return
    }
    this.destroyed = true
    this.dao.destroy()
  }

  public fetch = async (): Promise<Configs> => {
    const ids = this.getIds()

    const configs = await this.dao.fetchConfigs(ids, this.props.cookiesManager.getPgdidCookie())

    // Build a map with campaign_id as key and config as value
    return configs.reduce((acc, config) => {
      acc[config.campaignId] = config
      return acc
    }, {} as Configs)
  }

  private getIds = (): string[] => {
    const activeCampaignId = this.getIdFromActiveUrl()
    const allIds = [...this.getIdsFromOrgElements(), ...this.getIdsFromClassyElements()]

    if (activeCampaignId) {
      allIds.push(activeCampaignId)
    }

    const nudgeTrayCampaignId = this.getIdFromNudgeTrayState()

    if (nudgeTrayCampaignId) {
      allIds.push(nudgeTrayCampaignId)
    }

    const uniqueIds = [...new Set(allIds)]
    return uniqueIds
  }

  private getIdFromActiveUrl = (): string | null => {
    try {
      const params = new URLSearchParams(this.props.win.location.search)
      return params.get(CLASSY_HREF)
    } catch (_) {
      return null
    }
  }

  private getIdFromNudgeTrayState = (): string | null => {
    try {
      const rawState = this.props.win.localStorage.getItem('cl-nudge-tray')

      if (!rawState) {
        return null
      }

      const parsedState = JSON.parse(rawState) as { campaign?: unknown }

      if (typeof parsedState.campaign === 'string') {
        return parsedState.campaign
      }

      return null
    } catch (_) {
      return null
    }
  }

  private getIdsFromOrgElements = (): string[] => {
    const els: NodeListOf<HTMLAnchorElement> = document.querySelectorAll(`a[href*=${CLASSY_HREF}]`)

    const campaignIds: string[] = []

    els.forEach((el) => {
      const href = el.getAttribute('href')

      if (!href) {
        return
      }

      const campaignId = this._extractCampaignId(href)

      if (!campaignId) {
        return
      }

      campaignIds.push(campaignId)
    })

    return campaignIds
  }

  private getIdsFromClassyElements = (): string[] => {
    const els: NodeListOf<HTMLElement> = document.querySelectorAll(`[${CLASSY_ATTR}]`)

    const campaignIds: string[] = []

    if (!els.length) {
      return campaignIds
    }

    els.forEach((el) => {
      const campaignId = el.getAttribute(CLASSY_ATTR)
      if (campaignId) {
        campaignIds.push(campaignId)
      }
    })

    return campaignIds
  }

  private _extractCampaignId = (url: string): string | null => {
    // Check if the string starts directly with "campaign="
    if (url.startsWith(`${CLASSY_HREF}=`)) {
      return url.split('=')[1] || null
    }

    // Check if the string starts with "/?" or just "?"
    if (url.startsWith('/?') || url.startsWith('?')) {
      url = 'https://placeholder.com' + url
    }

    // Parse the URL and attempt to retrieve the 'campaign' parameter
    try {
      const urlObj = new URL(url)
      return urlObj.searchParams.get(CLASSY_HREF)
    } catch (_) {
      return null
    }
  }
}
